import React from 'react';
import PropTypes from 'prop-types';
import { Box, InputAdornment, Switch, TextField } from '@mui/material';
import ConfigCard from './ConfigCard';
import { PdiRow } from './_PdiHelpers';

/**
 * "Outlet mode" card — controls the dedicated outlet/sale page. Sits in the
 * Navigation modal underneath HeaderContentCard. Two settings:
 *   - minDiscount      : products need at least this discount % to be considered outlet
 *   - hideFromExplore  : when on, those products vanish from the regular Explore grid
 */
const OutletModeCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    const minDiscount = get('minDiscount', 10);

    return (
        <ConfigCard
            anchor="cfg-card-outlet-mode"
            title={tr('cfg_outlet_title', 'Outlet mode')}
            description={tr('cfg_outlet_desc', 'Settings for the dedicated outlet/sale page.')}
            theme={theme}
        >
            <PdiRow
                label={tr('cfg_outlet_min_discount_label', 'Minimum discount to qualify as outlet')}
                help={tr('cfg_outlet_min_discount_help', 'Only products with at least this discount percentage will show on the outlet page')}
                control={
                    <TextField
                        size="small"
                        type="number"
                        value={minDiscount}
                        onChange={(e) => {
                            const n = e.target.value === '' ? '' : Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                            set('minDiscount')(n);
                        }}
                        inputProps={{ min: 0, max: 100, style: { width: 56, textAlign: 'right' } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {tr('cfg_outlet_min_discount_suffix', '% off')}
                                </InputAdornment>
                            ),
                        }}
                    />
                }
            />

            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />

            <PdiRow
                label={tr('cfg_outlet_hide_from_explore_label', 'Hide outlet products on the regular Explore page')}
                help={tr('cfg_outlet_hide_from_explore_help', 'Discounted products only appear on the outlet page, not in the normal product grid')}
                control={
                    <Switch
                        color="success"
                        size="small"
                        checked={!!get('hideFromExplore', false)}
                        onChange={(e) => set('hideFromExplore')(e.target.checked)}
                    />
                }
            />
        </ConfigCard>
    );
};

OutletModeCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default OutletModeCard;
